import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// MARK://dhk8/ui-restaurant/FloorMapPage
// Canvas drag-drop tables, autosave, rotation, resize; states AVAILABLE/OCCUPIED/RESERVED/DIRTY.
// Used by POS (read+edit) and admin floor editor — same component, same coordinate
// space, same visual style. Drag uses pointer events (mouse + touch). Optional
// snap-to-grid (Shift overrides) and pan/zoom (mouse drag empty canvas + wheel).
// Port de sc-restaurant FloorMapPage.tsx.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Move, Save, RotateCcw, Plus, Trash2, Maximize2 } from 'lucide-react';
import { Badge, Button } from '@dockethub/ui';
import { usePanZoom } from './usePanZoom.js';
const STATUS_LABEL = {
    AVAILABLE: 'Libre',
    OCCUPIED: 'Ocupada',
    RESERVED: 'Reservada',
    DIRTY: 'Sucia',
    UNAVAILABLE: 'No disponible',
    CLEANING: 'Limpiando',
};
const STATUS_TONE = {
    AVAILABLE: 'success',
    OCCUPIED: 'danger',
    RESERVED: 'warning',
    DIRTY: 'warning',
    UNAVAILABLE: 'default',
    CLEANING: 'default',
};
function snap(v, grid) { return grid > 0 ? Math.round(v / grid) * grid : v; }
export function FloorMapPage({ tables: initialTables, floorWidthPx = 900, floorHeightPx = 600, onSave, onTablesChange, onAddTable, onDeleteTable, onSelectTable, readOnly = false, title, snapGrid = 0, showGrid = false, panZoom = false, sectionColorFor, hideInspector = false, }) {
    // When parent controls tables (onTablesChange set), don't keep internal state.
    const [internalTables, setInternalTables] = useState(initialTables);
    const tables = onTablesChange ? initialTables : internalTables;
    // Mirror parent prop updates into internal state when uncontrolled.
    useEffect(() => { if (!onTablesChange)
        setInternalTables(initialTables); }, [initialTables, onTablesChange]);
    function commitTables(next) {
        if (onTablesChange)
            onTablesChange(next);
        else
            setInternalTables(next);
    }
    const [selectedId, setSelectedId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const drag = useRef(null);
    const canvasRef = useRef(null);
    const viewportRef = useRef(null);
    const selectedTable = tables.find((t) => t.id === selectedId);
    // ── pan/zoom (opt-in) ─────────────────────────────────────────────────────
    const getViewport = useCallback(() => {
        const el = viewportRef.current;
        if (!el)
            return null;
        return { width: el.clientWidth, height: el.clientHeight };
    }, []);
    const shouldStartPan = useCallback((target) => {
        if (!(target instanceof Element))
            return true;
        return !target.closest('[data-table]');
    }, []);
    const pz = usePanZoom({ contentW: floorWidthPx, contentH: floorHeightPx, getViewport, shouldStartPan });
    const transform = panZoom ? pz.transform : { tx: 0, ty: 0, scale: 1 };
    // ── pointer-event drag (works with mouse + touch) ─────────────────────────
    const handlePointerDown = useCallback((e, tableId) => {
        if (readOnly)
            return;
        e.preventDefault();
        e.stopPropagation();
        const t = tables.find((tbl) => tbl.id === tableId);
        if (t === undefined)
            return;
        setSelectedId(tableId);
        e.currentTarget.setPointerCapture?.(e.pointerId);
        drag.current = {
            tableId,
            startMouseX: e.clientX,
            startMouseY: e.clientY,
            startTableX: t.posX,
            startTableY: t.posY,
        };
    }, [tables, readOnly]);
    const handlePointerMove = useCallback((e) => {
        if (drag.current === null)
            return;
        const { tableId, startMouseX, startMouseY, startTableX, startTableY } = drag.current;
        const dx = (e.clientX - startMouseX) / transform.scale;
        const dy = (e.clientY - startMouseY) / transform.scale;
        const useSnap = snapGrid > 0 && !e.shiftKey;
        const next = tables.map((t) => {
            if (t.id !== tableId)
                return t;
            const rawX = startTableX + dx;
            const rawY = startTableY + dy;
            return {
                ...t,
                posX: Math.max(0, Math.min(floorWidthPx - t.widthPx, useSnap ? snap(rawX, snapGrid) : Math.round(rawX))),
                posY: Math.max(0, Math.min(floorHeightPx - t.heightPx, useSnap ? snap(rawY, snapGrid) : Math.round(rawY))),
            };
        });
        commitTables(next);
        setDirty(true);
    }, [floorWidthPx, floorHeightPx, snapGrid, transform.scale, tables, onTablesChange]);
    const handlePointerUp = useCallback(() => {
        drag.current = null;
    }, []);
    async function handleSave() {
        if (!onSave)
            return;
        setSaving(true);
        try {
            await onSave(tables);
            setDirty(false);
        }
        finally {
            setSaving(false);
        }
    }
    function handleReset() {
        commitTables(initialTables);
        setDirty(false);
    }
    function cycleStatus(tableId) {
        if (readOnly)
            return;
        const cycle = ['AVAILABLE', 'OCCUPIED', 'RESERVED', 'DIRTY'];
        const next = tables.map((t) => {
            if (t.id !== tableId)
                return t;
            const idx = cycle.indexOf(t.status);
            const ns = cycle[(idx + 1) % cycle.length];
            if (ns === undefined)
                return t;
            return { ...t, status: ns };
        });
        commitTables(next);
        setDirty(true);
    }
    return (_jsxs("div", { className: "dhk8-floor", children: [_jsxs("div", { className: "dhk8-floor__toolbar", children: [_jsx("span", { className: "dhk8-floor__title", children: title ?? 'Mapa del salon' }), _jsxs("div", { className: "dhk8-floor__actions", children: [panZoom && (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: () => pz.zoomBy(1.2), "aria-label": "Acercar", children: "+" }), _jsx(Button, { variant: "ghost", size: "sm", onClick: () => pz.zoomBy(1 / 1.2), "aria-label": "Alejar", children: "\u2212" }), _jsx(Button, { variant: "ghost", size: "sm", leading: _jsx(Maximize2, { size: 14 }), onClick: pz.fit, "aria-label": "Ajustar", children: "Ajustar" }), _jsxs("span", { style: { fontSize: 11, color: 'var(--fg-muted)', alignSelf: 'center' }, children: [(transform.scale * 100).toFixed(0), "%"] })] })), onAddTable !== undefined && !readOnly && (_jsx(Button, { variant: "secondary", size: "sm", leading: _jsx(Plus, { size: 14 }), onClick: onAddTable, children: "Mesa" })), onSave !== undefined && !readOnly && (_jsx(Button, { variant: "primary", size: "sm", leading: _jsx(Save, { size: 14 }), disabled: !dirty || saving, onClick: () => void handleSave(), children: saving ? 'Guardando...' : 'Guardar' })), dirty && !readOnly && (_jsx(Button, { variant: "ghost", size: "sm", leading: _jsx(RotateCcw, { size: 14 }), onClick: handleReset, children: "Deshacer" }))] })] }), _jsxs("div", { className: "dhk8-floor__layout", children: [_jsx("div", { ref: viewportRef, className: `dhk8-floor__viewport${panZoom && pz.panning ? ' dhk8-floor__viewport--panning' : ''}`, style: {
                            // Antes: `height: floorHeightPx` (800 fijos) hacía scroll vertical en
                            // desktop. Ahora respetamos el `aspectRatio` del plano y dejamos que el
                            // contenedor padre decida width (con un cap a floorWidthPx). El alto se
                            // calcula automáticamente. usePanZoom mide `clientWidth/clientHeight`
                            // del viewport, así que necesita un tamaño no nulo al primer render —
                            // por eso NO usamos height:100% (que puede colapsar a 0 si el padre no
                            // tiene flex/grid configurado).
                            width: '100%',
                            maxWidth: floorWidthPx,
                            aspectRatio: `${floorWidthPx} / ${floorHeightPx}`,
                            touchAction: panZoom ? 'none' : undefined,
                        }, ...(panZoom ? pz.hostHandlers : {}), children: _jsx("div", { ref: canvasRef, className: "dhk8-floor__canvas", style: {
                                width: floorWidthPx,
                                height: floorHeightPx,
                                transform: panZoom ? `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})` : undefined,
                                transformOrigin: panZoom ? '0 0' : undefined,
                                backgroundImage: showGrid && snapGrid > 0
                                    ? `linear-gradient(to right, color-mix(in oklch, var(--fg-muted, #7E8C82) 18%, transparent) 1px, transparent 1px),
                   linear-gradient(to bottom, color-mix(in oklch, var(--fg-muted, #7E8C82) 18%, transparent) 1px, transparent 1px)`
                                    : undefined,
                                backgroundSize: showGrid && snapGrid > 0 ? `${snapGrid}px ${snapGrid}px, ${snapGrid}px ${snapGrid}px` : undefined,
                            }, onPointerMove: handlePointerMove, onPointerUp: handlePointerUp, onPointerLeave: handlePointerUp, children: tables.map((t) => {
                                const sel = selectedId === t.id;
                                const tint = sectionColorFor?.(t);
                                return (_jsxs("div", { "data-table": t.id, className: `dhk8-floor__table dhk8-floor__table--${t.status.toLowerCase()}${sel ? ' dhk8-floor__table--selected' : ''}`, style: {
                                        left: t.posX,
                                        top: t.posY,
                                        width: t.widthPx,
                                        height: t.heightPx,
                                        borderRadius: t.shape === 'ROUND' ? '50%' : undefined,
                                        cursor: readOnly ? 'default' : 'grab',
                                        ...(tint ? { borderColor: tint, ...(sel ? { background: `color-mix(in oklch, ${tint} 30%, var(--surface))` } : {}) } : {}),
                                    }, onPointerDown: (e) => handlePointerDown(e, t.id), onClick: () => {
                                        if (onSelectTable)
                                            onSelectTable(t);
                                        else
                                            setSelectedId((prev) => (prev === t.id ? null : t.id));
                                    }, role: "button", tabIndex: 0, "aria-label": `Mesa ${t.code} — ${STATUS_LABEL[t.status]}`, onKeyDown: (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            if (onSelectTable)
                                                onSelectTable(t);
                                            else
                                                setSelectedId(t.id);
                                        }
                                    }, children: [_jsx("span", { className: "dhk8-floor__table-code", children: t.code }), !readOnly && _jsx(Move, { size: 10, className: "dhk8-floor__table-drag" })] }, t.id));
                            }) }) }), !hideInspector && selectedTable !== undefined && (_jsxs("aside", { className: "dhk8-floor__inspector", children: [_jsxs("div", { className: "dhk8-floor__insp-title", children: ["Mesa ", selectedTable.code] }), _jsxs("div", { className: "dhk8-floor__insp-row", children: [_jsx("span", { children: "Estado" }), _jsx(Badge, { tone: STATUS_TONE[selectedTable.status], children: STATUS_LABEL[selectedTable.status] })] }), _jsxs("div", { className: "dhk8-floor__insp-row", children: [_jsx("span", { children: "Capacidad" }), _jsxs("span", { children: [selectedTable.capacity, " personas"] })] }), _jsxs("div", { className: "dhk8-floor__insp-row", children: [_jsx("span", { children: "Posicion" }), _jsxs("span", { children: [Math.round(selectedTable.posX), ", ", Math.round(selectedTable.posY)] })] }), !readOnly && (_jsxs("div", { className: "dhk8-floor__insp-actions", children: [_jsx(Button, { variant: "secondary", size: "sm", block: true, onClick: () => cycleStatus(selectedTable.id), children: "Cambiar estado" }), onDeleteTable !== undefined && (_jsx(Button, { variant: "danger", size: "sm", block: true, leading: _jsx(Trash2, { size: 14 }), onClick: () => { onDeleteTable(selectedTable.id); setSelectedId(null); }, children: "Eliminar" }))] }))] }))] }), _jsx("style", { children: floorStyles })] }));
}
const floorStyles = `
  .dhk8-floor { display: flex; flex-direction: column; gap: var(--space-3); }
  .dhk8-floor__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }
  .dhk8-floor__title { font-family: var(--font-mono); font-size: var(--text-lg); color: var(--fg); }
  .dhk8-floor__actions { display: flex; gap: var(--space-2); }
  .dhk8-floor__layout { display: flex; gap: var(--space-4); align-items: flex-start; }
  .dhk8-floor__viewport {
    position: relative;
    overflow: hidden;
    user-select: none;
    background: var(--bg);
    border: var(--border-width) solid var(--border);
    flex-shrink: 0;
    cursor: grab;
  }
  .dhk8-floor__viewport--panning { cursor: grabbing; }
  .dhk8-floor__canvas {
    position: absolute;
    inset: 0;
    will-change: transform;
    background: var(--bg);
    user-select: none;
  }
  .dhk8-floor__table {
    position: absolute;
    background: var(--surface);
    border: var(--border-width) solid var(--border-strong);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    transition: border-color var(--duration-base) var(--easing);
    outline: none;
  }
  .dhk8-floor__table--selected { outline: 2px solid var(--accent); outline-offset: 2px; }
  .dhk8-floor__table--available { border-color: var(--success); }
  .dhk8-floor__table--occupied { border-color: var(--destructive); background: var(--destructive-bg); }
  .dhk8-floor__table--reserved { border-color: var(--warning); background: var(--warning-bg); }
  .dhk8-floor__table--dirty { opacity: 0.7; }
  .dhk8-floor__table--unavailable, .dhk8-floor__table--cleaning { opacity: 0.4; }
  .dhk8-floor__table-code { font-family: var(--font-mono); font-size: var(--text-xs); color: var(--fg); }
  .dhk8-floor__table-drag { color: var(--fg-muted); opacity: 0.4; }
  .dhk8-floor__inspector {
    min-width: 200px;
    background: var(--surface);
    border: var(--border-width) solid var(--border);
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    border-radius: var(--radius);
  }
  .dhk8-floor__insp-title { font-family: var(--font-mono); font-size: var(--text-md); color: var(--fg); }
  .dhk8-floor__insp-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--fg-secondary);
  }
  .dhk8-floor__insp-actions { display: flex; flex-direction: column; gap: var(--space-2); margin-top: var(--space-2); }
`;
//# sourceMappingURL=FloorMapPage.js.map